import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db, auth, storage } from '../../firebase';
import {
    collection, addDoc, getDocs, updateDoc, deleteDoc,
    doc, query, where, getDoc, serverTimestamp, orderBy
} from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import Webcam from "react-webcam";
import Swal from 'sweetalert2';

// MUI Components
import {
    Paper, Typography, TextField, Button, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Pagination, Divider, Dialog, DialogTitle, DialogContent,
    DialogActions, Grid, MenuItem, Autocomplete, Stack, Chip
} from '@mui/material';

import {
    Delete as DeleteIcon,
    Search as SearchIcon,
    Add as AddIcon,
    Close as CloseIcon,
    CameraAlt as CameraIcon,
    Autorenew as RetakeIcon,
    Visibility as ViewIcon,
    AccountBalanceWallet
} from '@mui/icons-material';

function Storeinventory() {

    const [inventory, setInventory] = useState([]);
    const [students, setStudents] = useState([]);
    const [products, setProducts] = useState([]);
    const [schoolId, setSchoolId] = useState(null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [open, setOpen] = useState(false);
    const [viewPhoto, setViewPhoto] = useState(null);

    const [walletBalance, setWalletBalance] = useState(0);

    // FILTERS
    const [searchTerm, setSearchTerm] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // PAGINATION
    const [page, setPage] = useState(1);
    const itemsPerPage = 10;

    // FORM
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [issueDate, setIssueDate] = useState(
        new Date().toISOString().split('T')[0]
    );

    const [items, setItems] = useState([
        {
            productId: '',
            qty: '',
            rate: '',
            total: 0,
            payType: 'Payable'
        }
    ]);

    const [paymentSource, setPaymentSource] = useState('Manual');
    const [payMode, setPayMode] = useState('Cash');

    // CAMERA
    const webcamRef = useRef(null);
    const [imgSrc, setImgSrc] = useState(null);
    const [showCamera, setShowCamera] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {

                const userSnap = await getDoc(doc(db, "users", user.uid));

                if (userSnap.exists()) {

                    const sId = userSnap.data()?.schoolId;

                    setSchoolId(sId);

                    await fetchInitialData(sId);
                }
            }

            setLoading(false);
        });

        return () => unsubscribe();

    }, []);

    useEffect(() => {

        if (selectedStudent?.studentId) {
            fetchWalletBalance(selectedStudent.studentId);
        } else {
            setWalletBalance(0);
        }

    }, [selectedStudent]);

    const fetchWalletBalance = async (studentId) => {

        const q = query(
            collection(db, "wallet_transactions"),
            where("studentId", "==", studentId)
        );

        const snap = await getDocs(q);

        const balance = snap.docs.reduce((acc, curr) => {

            return curr.data().type === "Credit"
                ? acc + Number(curr.data().amount)
                : acc - Number(curr.data().amount);

        }, 0);

        setWalletBalance(balance);
    };

    const fetchInitialData = async (sId) => {

        // STUDENTS
        const admSnap = await getDocs(
            query(
                collection(db, "admission"),
                where("schoolId", "==", sId),
                where("status", "==", "Active")
            )
        );

        const studentList = await Promise.all(
            admSnap.docs.map(async (adm) => {

                const admData = adm.data();

                const studentSnap = await getDoc(
                    doc(db, "students", admData.studentId)
                );

                const studentData = studentSnap.data();

                return {
                    id: adm.id,
                    studentId: admData.studentId,
                    label: `${studentData?.firstname || ""} ${studentData?.fathername || ""} ${studentData?.surname || ""}`.toUpperCase()
                };

            })
        );

        setStudents(studentList);

        // PRODUCTS
        const productSnap = await getDocs(
            query(
                collection(db, "products"),
                where("schoolId", "==", sId)
            )
        );

        setProducts(
            productSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }))
        );

        fetchInventory(sId);
    };

    const fetchInventory = async (sId) => {

        setLoading(true);

        try {

            const q = query(
                collection(db, "studentInventory"),
                where("schoolId", "==", sId),
                orderBy("updatedAt", "desc")
            );

            const snap = await getDocs(q);

            setInventory(
                snap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
            );

        } catch (err) {
            console.log(err);
        }

        setLoading(false);
    };

    const handleItemChange = (index, field, value) => {

        const newItems = [...items];

        newItems[index][field] = value;

        if (
            field === "qty" ||
            field === "rate" ||
            field === "payType"
        ) {

            const qty = parseFloat(newItems[index].qty) || 0;
            const rate = parseFloat(newItems[index].rate) || 0;

            newItems[index].total =
                newItems[index].payType === "Payable"
                    ? qty * rate
                    : 0;
        }

        setItems(newItems);
    };

    const addItemRow = () => {

        setItems([
            ...items,
            {
                productId: '',
                qty: '',
                rate: '',
                total: 0,
                payType: 'Payable'
            }
        ]);
    };

    const removeItemRow = (index) => {

        const updated = items.filter((_, i) => i !== index);

        setItems(updated);
    };

    const grandTotal = items.reduce(
        (sum, item) => sum + (item.total || 0),
        0
    );

    const capture = useCallback(() => {

        const imageSrc = webcamRef.current.getScreenshot();

        setImgSrc(imageSrc);

        setShowCamera(false);

    }, [webcamRef]);

    const handleSubmit = async (e) => {

        e.preventDefault();

        if (!selectedStudent) {
            return Swal.fire("Error", "Select Student", "error");
        }

        if (
            paymentSource === "Wallet" &&
            walletBalance < grandTotal
        ) {
            return Swal.fire(
                "Insufficient Balance",
                `Wallet Balance ₹${walletBalance}`,
                "warning"
            );
        }

        setSaving(true);

        try {

            let photoUrl = imgSrc || null;

            if (imgSrc && imgSrc.startsWith("data:image")) {

                const storageRef = ref(
                    storage,
                    `inventory/${schoolId}/${Date.now()}.jpg`
                );

                await uploadString(storageRef, imgSrc, "data_url");

                photoUrl = await getDownloadURL(storageRef);
            }

            const payload = {
                studentAdmissionId: selectedStudent.id,
                studentId: selectedStudent.studentId,
                studentName: selectedStudent.label,
                issueDate,
                items,
                grandTotal,
                paymentSource,
                payMode: paymentSource === "Wallet"
                    ? "Wallet"
                    : payMode,
                photoUrl,
                schoolId,
                updatedAt: serverTimestamp(),
                createdAt: serverTimestamp()
            };

            await addDoc(
                collection(db, "studentInventory"),
                payload
            );

            // WALLET ENTRY
            if (
                paymentSource === "Wallet" &&
                grandTotal > 0
            ) {

                await addDoc(
                    collection(db, "wallet_transactions"),
                    {
                        studentId: selectedStudent.studentId,
                        amount: grandTotal,
                        type: "Debit",
                        description: `Inventory Issue`,
                        timestamp: serverTimestamp(),
                        dateTime: new Date().toLocaleString()
                    }
                );
            }

            Swal.fire(
                "Success",
                "Inventory Saved",
                "success"
            );

            handleClose();

            fetchInventory(schoolId);

        } catch (err) {

            console.log(err);

            Swal.fire(
                "Error",
                err.message,
                "error"
            );
        }

        setSaving(false);
    };

    const handleClose = () => {

        setOpen(false);

        setSelectedStudent(null);

        setItems([
            {
                productId: '',
                qty: '',
                rate: '',
                total: 0,
                payType: 'Payable'
            }
        ]);

        setPaymentSource("Manual");

        setImgSrc(null);
    };

    // DATE FILTER
    const filteredInventory = inventory.filter((row) => {

        const matchesSearch =
            row.studentName?.toLowerCase()
                .includes(searchTerm.toLowerCase());

        const rowDate = row.issueDate;

        let matchesDate = true;

        if (fromDate && rowDate < fromDate) {
            matchesDate = false;
        }

        if (toDate && rowDate > toDate) {
            matchesDate = false;
        }

        return matchesSearch && matchesDate;
    });

    const paginatedData = filteredInventory.slice(
        (page - 1) * itemsPerPage,
        page * itemsPerPage
    );

    const formatDate = (dateStr) => {

        if (!dateStr) return "";

        const [year, month, day] = dateStr.split("-");

        return `${day}-${month}-${year}`;
    };

    return (

        <Box p={1}>

            <Paper sx={{ p: 3, borderRadius: 3 }}>

                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                >

                    <Typography variant="h5" fontWeight="700">
                        Store Inventory Report
                    </Typography>

                </Stack>

                {/* FILTERS */}

                <Grid container spacing={2} mb={3}>

                    <Grid item xs={12} md={4}>

                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search Student..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setPage(1);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <SearchIcon
                                        fontSize="small"
                                        sx={{ mr: 1 }}
                                    />
                                )
                            }}
                        />

                    </Grid>

                    <Grid item xs={12} md={3}>

                        <TextField
                            fullWidth
                            size="small"
                            label="From Date"
                            type="date"
                            value={fromDate}
                            onChange={(e) => {
                                setFromDate(e.target.value);
                                setPage(1);
                            }}
                            InputLabelProps={{ shrink: true }}
                        />

                    </Grid>

                    <Grid item xs={12} md={3}>

                        <TextField
                            fullWidth
                            size="small"
                            label="To Date"
                            type="date"
                            value={toDate}
                            onChange={(e) => {
                                setToDate(e.target.value);
                                setPage(1);
                            }}
                            InputLabelProps={{ shrink: true }}
                        />

                    </Grid>

                    <Grid item xs={12} md={2}>

                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                                setFromDate("");
                                setToDate("");
                                setSearchTerm("");
                            }}
                        >
                            Clear
                        </Button>

                    </Grid>

                </Grid>

                {/* TABLE */}

                <TableContainer component={Paper} variant="outlined">

                    <Table size="small">

                        <TableHead sx={{ bgcolor: "#f8fafc" }}>

                            <TableRow>

                                <TableCell>
                                    <b>Sr No</b>
                                </TableCell>

                                <TableCell>
                                    <b>Date</b>
                                </TableCell>

                                <TableCell>
                                    <b>Student</b>
                                </TableCell>

                                <TableCell>
                                    <b>Items</b>
                                </TableCell>

                                <TableCell>
                                    <b>Total</b>
                                </TableCell>

                                <TableCell>
                                    <b>Payment</b>
                                </TableCell>

                                <TableCell align="center">
                                    <b>Photo</b>
                                </TableCell>

                           

                            </TableRow>

                        </TableHead>

                        <TableBody>

                            {loading ? (

                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        Loading...
                                    </TableCell>
                                </TableRow>

                            ) : paginatedData.length === 0 ? (

                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        No Records Found
                                    </TableCell>
                                </TableRow>

                            ) : (

                                paginatedData.map((row, index) => (

                                    <TableRow key={row.id} hover>

                                        <TableCell>
                                            {(page - 1) * itemsPerPage + index + 1}
                                        </TableCell>

                                        <TableCell>
                                            {formatDate(row.issueDate)}
                                        </TableCell>

                                        <TableCell>
                                            {row.studentName}
                                        </TableCell>

                                        <TableCell>
                                            {row.items?.length || 0} Items
                                        </TableCell>

                                        <TableCell>
                                            ₹{row.grandTotal}
                                        </TableCell>

                                        <TableCell>

                                            <Chip
                                                label={row.payMode}
                                                size="small"
                                                color={
                                                    row.payMode === "Wallet"
                                                        ? "secondary"
                                                        : "primary"
                                                }
                                            />

                                        </TableCell>

                                        <TableCell align="center">

                                            {row.photoUrl && (

                                                <IconButton
                                                    color="info"
                                                    onClick={() =>
                                                        setViewPhoto(row.photoUrl)
                                                    }
                                                >
                                                    <ViewIcon fontSize="small" />
                                                </IconButton>

                                            )}

                                        </TableCell>

                                    </TableRow>

                                ))
                            )}

                        </TableBody>

                    </Table>

                </TableContainer>

                {/* PAGINATION */}

                <Stack
                    direction="row"
                    justifyContent="center"
                    mt={3}
                >

                    <Pagination
                        count={Math.ceil(
                            filteredInventory.length / itemsPerPage
                        )}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        color="primary"
                    />

                </Stack>

            </Paper>

            {/* ADD DIALOG */}

            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="lg"
            >

                <DialogTitle>
                    Inventory Entry
                </DialogTitle>

                <form onSubmit={handleSubmit}>

                    <DialogContent dividers>

                        <Grid container spacing={2}>

                            <Grid item xs={12} md={5}>

                                <Autocomplete
                                    options={students}
                                    getOptionLabel={(o) => o.label || ""}
                                    value={selectedStudent}
                                    onChange={(e, value) =>
                                        setSelectedStudent(value)
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Student"
                                            size="small"
                                            required
                                        />
                                    )}
                                />

                            </Grid>

                            <Grid item xs={12} md={3}>

                                <TextField
                                    fullWidth
                                    label="Issue Date"
                                    type="date"
                                    size="small"
                                    value={issueDate}
                                    onChange={(e) =>
                                        setIssueDate(e.target.value)
                                    }
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                />

                            </Grid>

                            <Grid item xs={12} md={4}>

                                <Paper
                                    sx={{
                                        p: 1,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        bgcolor: '#f0fdf4'
                                    }}
                                >

                                    <AccountBalanceWallet
                                        sx={{ color: 'green', mr: 1 }}
                                    />

                                    <Typography fontWeight="bold">
                                        Wallet : ₹{walletBalance}
                                    </Typography>

                                </Paper>

                            </Grid>

                        </Grid>

                    </DialogContent>

                    <DialogActions>

                        <Button onClick={handleClose}>
                            Cancel
                        </Button>

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={saving}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>

                    </DialogActions>

                </form>

            </Dialog>

            {/* PHOTO VIEW */}

            <Dialog
                open={Boolean(viewPhoto)}
                onClose={() => setViewPhoto(null)}
                maxWidth="md"
            >

                <IconButton
                    onClick={() => setViewPhoto(null)}
                    sx={{
                        position: 'absolute',
                        right: 5,
                        top: 5,
                        bgcolor: '#fff'
                    }}
                >
                    <CloseIcon />
                </IconButton>

                {viewPhoto && (

                    <img
                        src={viewPhoto}
                        alt="Inventory"
                        style={{
                            width: '100%',
                            maxHeight: '90vh',
                            objectFit: 'contain'
                        }}
                    />

                )}

            </Dialog>

        </Box>
    );
}

export default Storeinventory;